import React, { useEffect, useState } from "react";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { OrderDialogContent } from "~/pages/purchase-orders/components/order-detail/OrderDialogContent";
import { useOrderCreditUtilization } from "~/pages/purchase-orders/hooks/useOrderCreditUtilization";
import { useOrderDialogUsers } from "~/pages/purchase-orders/hooks/useOrderDialogUsers";
import { useStatusHistory } from "~/pages/purchase-orders/hooks/useStatusHistory";
import { ClientInfo } from "~/pages/purchase-orders/hooks/utils/clientUtils";
import { Seller, fetchSellers } from "~/services/sellerService";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest, SalesRequestItem } from "~/types/client";

interface OrderDetailDialogProps {
  selectedOrder: SalesRequest;
  setSelectedOrder: (order: SalesRequest | null) => void;
  getSellerInfo: (sellerId: string) => {
    name: string;
    lastname: string;
    email: string;
  };
  getApproverRejecterInfo?: (approverId?: string) => {
    name: string;
    lastname: string;
    email: string;
    role: string;
  };
  clientNames: Record<string, string>;
  items: SalesRequestItem[];
  onOrderStatusChange?: () => void;
  clientRiskStatus?: Record<string, boolean>;
  clientInfo?: Record<string, ClientInfo>;
}

// Renamed component to OrderDetailDrawer but keeping the export name for backward compatibility
const OrderDetailDrawer: React.FC<OrderDetailDialogProps> = ({
  selectedOrder,
  setSelectedOrder,
  getSellerInfo,
  getApproverRejecterInfo = () => ({
    name: "Sistema",
    lastname: "",
    email: "",
    role: "system",
  }),
  clientNames,
  items,
  onOrderStatusChange,
  clientRiskStatus = {},
  clientInfo = {},
}) => {
  const [isApproveSheetOpen, setIsApproveSheetOpen] = useState(false);
  const [isRejectSheetOpen, setIsRejectSheetOpen] = useState(false);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [isLoadingSellers, setIsLoadingSellers] = useState(false);

  const profileStore = useProfileStore();
  const isSuperAdmin = profileStore.role === "superadmin";

  // Get client credit data
  const { clientCreditData, loading: creditLoading } =
    useOrderCreditUtilization(selectedOrder.clientId);

  // Get order status history
  const { history, loading: statusHistoryLoading } = useStatusHistory(
    selectedOrder.id
  );

  // Get users information for status history
  const usersById = useOrderDialogUsers(history, getSellerInfo);

  // Fetch sellers if user is super admin
  useEffect(() => {
    const loadSellers = async () => {
      if (isSuperAdmin) {
        setIsLoadingSellers(true);
        try {
          const sellersData = await fetchSellers();
          setSellers(sellersData);
        } catch (error) {
          console.error("Error fetching sellers:", error);
          toast({
            title: "Error",
            description: "No se pudieron cargar los vendedores.",
            variant: "destructive",
          });
        } finally {
          setIsLoadingSellers(false);
        }
      }
    };

    loadSellers();
  }, [isSuperAdmin]);

  // Handle seller change
  const handleSellerChange = async (newSellerId: string) => {
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          created_by: newSellerId,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Add status history entry
      await supabase.from("sales_request_status_history").insert({
        sales_request_id: selectedOrder.id,
        new_status: selectedOrder.status,
        changed_by: profileStore.id,
        changed_at: new Date().toISOString(),
        notes: `Vendedor cambiado a ${sellers.find((s) => s.id === newSellerId)?.name} ${sellers.find((s) => s.id === newSellerId)?.lastname}`,
      });

      // Update the local state
      const updatedOrder = {
        ...selectedOrder,
        createdBy: newSellerId,
        updatedAt: new Date().toISOString(),
      };
      setSelectedOrder(updatedOrder);

      toast({
        title: "Vendedor actualizado",
        description:
          "El vendedor de la nota de venta ha sido cambiado exitosamente.",
        variant: "default",
      });

      // Refresh the page data if callback provided
      if (onOrderStatusChange) {
        onOrderStatusChange();
      }
    } catch (error) {
      console.error("Error updating seller:", error);
      toast({
        title: "Error",
        description: "No se pudo cambiar el vendedor de la nota de venta.",
        variant: "destructive",
      });
    }
  };

  return (
    <OrderDialogContent
      selectedOrder={selectedOrder}
      setSelectedOrder={setSelectedOrder}
      getSellerInfo={getSellerInfo}
      getApproverRejecterInfo={getApproverRejecterInfo}
      clientNames={clientNames}
      items={items}
      onOrderStatusChange={onOrderStatusChange}
      isApproveSheetOpen={isApproveSheetOpen}
      setIsApproveSheetOpen={setIsApproveSheetOpen}
      isRejectSheetOpen={isRejectSheetOpen}
      setIsRejectSheetOpen={setIsRejectSheetOpen}
      clientRiskChecked={clientRiskStatus?.[selectedOrder.clientId] ?? true}
      clientCreditData={clientCreditData}
      creditLoading={creditLoading}
      statusHistory={history}
      loadingStatusHistory={statusHistoryLoading}
      usersById={usersById}
      useDrawer={true} // Add new prop to indicate we want to use a drawer
      canChangeSeller={isSuperAdmin}
      sellers={sellers}
      selectedSellerId={selectedOrder.createdBy}
      handleSellerChange={handleSellerChange}
      isSubmitting={isLoadingSellers}
      clientInfo={clientInfo}
    />
  );
};

export default OrderDetailDrawer;
